





























































































import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import MiraComparisonsDialogComponent from "@/components/mira/MiraComparisonsDialog.vue";
import { MiraComparisonKindOption } from "@/data/models/MiraSavedComparison";
import { mayEditMiraLandingChoice } from "@/utils/MiraLandingChoice";
import { miraPeriodGroup } from "@/utils/MiraDataQuality";
import { savedComparisonOption } from "@/utils/MiraSavedComparisons";

/**
 * The top of every Mira page: the period picker (a calendar) and the two comparison pickers (upwards,
 * then downwards with the filter icon) in the app bar, and the sticky header with the period, what it is compared with, and the run
 * date. `v-model` is the chosen period key.
 */
@Component({
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "mira-comparisons-dialog": MiraComparisonsDialogComponent,
  },
})
export default class MiraScreenHeaderComponent extends Vue {
  /** The chosen period key. */
  @Prop({ required: true })
  value!: string;

  @Prop({ required: true })
  periods!: string[];

  @Prop({ required: true })
  periodName!: (period: string) => string;

  @Prop({ required: true })
  periodDescription!: string;

  @Prop({ default: "" })
  comparisonDescription!: string;

  @Prop({ default: "" })
  generatedAtDescription!: string;

  /** The header card's colour, themed by the screen. */
  @Prop({ required: true })
  color!: string;

  /**
   * A period's heading in the calendar: Zeitraum, Jahre. Each period has one comparison of its own, so the
   * calendar offers no "Vergleichen mit" (Hans, #2611021, 2026-11-04), and with five periods its groups no
   * longer fold.
   */
  periodGroupName(period: string): string {
    return this.$t(`mira.periods.groups.${miraPeriodGroup(period)}`).toString();
  }

  /** The comparisons upwards (Markt, Gruppe); one option hides the icon (only "Kein Vergleich"). */
  @Prop({ default: () => [] })
  upwardOptions!: string[];

  /** The chosen comparison upwards. */
  @Prop({ default: "" })
  upwardValue!: string;

  /** The comparisons downwards (Mandanten, Standorte); one option hides the icon. */
  @Prop({ default: () => [] })
  downwardOptions!: string[];

  /** The chosen comparison downwards. */
  @Prop({ default: "" })
  downwardValue!: string;

  /** Names a comparison option for the picker. */
  @Prop({ default: () => (value: string) => value })
  comparisonName!: (value: string) => string;

  /** "Vergleich: Mandanten der Gruppe", appended to the first header line when a comparison is on. */
  @Prop({ default: "" })
  comparisonChoiceLabel!: string;

  /** The group membership caveat under the header, or empty where it does not apply. */
  @Prop({ default: "" })
  membershipNote!: string;

  /**
   * What this client compares downwards (Standorte or Mandanten, Versicherer), each with what it can pick, so
   * its saved comparisons can be managed here (#2610137 tasks 22 / 24); empty hides "Vergleiche verwalten".
   */
  @Prop({ default: () => [] })
  comparisonKinds!: MiraComparisonKindOption[];

  /** Loads what the candidates need (a group's members) before the dialog opens. */
  @Prop({ default: () => () => Promise.resolve() })
  prepareComparisons!: () => Promise<void>;

  @Ref("comparisonsDialog")
  comparisonsDialog!: MiraComparisonsDialogComponent;

  /** Opens "Vergleiche verwalten"; a comparison saved there is chosen when it closes. */
  async onManageComparisons() {
    if (this.comparisonKinds.length === 0) {
      return;
    }
    await this.prepareComparisons();
    await this.$nextTick();
    // The card settings' rule: never under Sicht auf, never without a user record to save on.
    const savedId = await this.comparisonsDialog.show(this.comparisonKinds, mayEditMiraLandingChoice());
    if (savedId) {
      this.$emit("downward-change", savedComparisonOption(savedId));
    }
  }
}
